import { useEffect, type ReactNode } from "react";
import { Phone } from "lucide-react";
import { useTrackedCTA } from "@/hooks/useTrackedCTA";
import logoUsa from "@/assets/logo_usa.webp";
import WhatsAppIcon from "@/components/ads/WhatsAppIcon";

interface AdsLandingLayoutProps {
  title: string;
  description: string;
  children: ReactNode;
}

/**
 * Minimal layout for Google Ads landing pages.
 * - noindex, nofollow
 * - No Header/Footer/Chat/CityPopup
 * - Sticky WhatsApp + Phone bar always visible
 */
const AdsLandingLayout = ({ title, description, children }: AdsLandingLayoutProps) => {
  const { handleWhatsAppClick, handlePhoneClick, whatsAppUrl, phone } = useTrackedCTA();

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("robots", "noindex, nofollow");

    // Remove any leftover canonical/JSON-LD from previous routes to avoid conflicts
    const removeIfExists = (selector: string) => {
      document.querySelectorAll(selector).forEach((el) => el.remove());
    };
    removeIfExists('link[rel="canonical"]');
    removeIfExists("#page-jsonld");
    removeIfExists("#breadcrumb-jsonld");

    return () => {
      const robots = document.querySelector('meta[name="robots"]');
      if (robots) robots.setAttribute("content", "index, follow");
    };
  }, [title, description]);

  return (
    <div className="min-h-screen flex flex-col bg-background font-sora pb-20 md:pb-0">
      {/* Top minimal brand bar - dark green pattern (matches site theme) */}
      <header
        className="border-b border-white/10 py-3 px-4 shadow-sm"
        style={{ background: "linear-gradient(135deg, #1d8a4f, #0d4a2a)" }}
      >
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <a
            href="/"
            aria-label="Ir para a página inicial da Dedetizadora USA"
            className="flex items-center gap-2 font-bold text-base md:text-lg text-white hover:opacity-90"
          >
            <img
              src={logoUsa}
              alt="Logo Dedetizadora USA"
              className="h-8 w-auto"
              width={61}
              height={32}
              fetchPriority="high"
              decoding="async"
            />
            <span className="hidden sm:inline">Dedetizadora USA</span>
          </a>
          <a
            href={phone}
            onClick={handlePhoneClick("ads_header")}
            aria-label="Ligar agora para a Dedetizadora USA"
            className="inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 transition-colors"
            style={{ backgroundColor: "#22c46a", color: "#0a1f14" }}
          >
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">Ligar agora</span>
            <span className="sm:hidden">Ligar</span>
          </a>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      {/* Minimal footer */}
      <footer className="bg-muted text-muted-foreground py-6 px-4 text-center text-xs">
        <p>© Dedetizadora USA - Ultra Serviços Ambientais</p>
        <p className="mt-1">Resultado satisfatório ou seu dinheiro de volta</p>
      </footer>

      {/* Sticky CTA bar - always visible */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border shadow-lg md:px-4">
        <div className="max-w-5xl mx-auto flex">
          <a
            href={whatsAppUrl}
            onClick={handleWhatsAppClick("ads_sticky_whatsapp")}
            aria-label="Falar no WhatsApp com a Dedetizadora USA"
            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 font-semibold text-white animate-cta-attention"
            style={{ backgroundColor: "#25D366" }}
          >
            <WhatsAppIcon size={22} />
            WhatsApp
          </a>
          <a
            href={phone}
            onClick={handlePhoneClick("ads_sticky_phone")}
            aria-label="Ligar para a Dedetizadora USA"
            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 font-semibold bg-primary text-primary-foreground animate-cta-attention"
          >
            <Phone className="w-5 h-5" />
            Ligar
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdsLandingLayout;
